import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Link2, Pencil, RefreshCw, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';
import ImageUploadField from '../components/ImageUploadField';
import EventCard from '../components/EventCard';
import TrashPanel from './TrashPanel';

function EventEditModal({ event, onClose, onSaved }) {
  const [f, setF] = useState({
    title: event.title || '', start_date: event.start_date || '', end_date: event.end_date || '',
    town: event.town || '', venue: event.venue || '', url: event.url || event.book_url || '',
    hero_image: event.hero_image || '', price: event.price || '', category: event.category || '',
    summary: event.summary || '', description: event.description || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api.patch(`/admin/events/${event.id}`, { ...f, end_date: f.end_date || null });
      onSaved();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Could not save');
      setBusy(false);
    }
  };
  const inp = 'w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm';
  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" data-testid="event-edit-modal">
      <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <h3 className="font-display text-2xl mb-4">Edit event</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="sm:col-span-2 text-xs font-semibold">Title<input value={f.title} onChange={(e) => set('title', e.target.value)} className={inp} data-testid="event-edit-title"/></label>
          <label className="text-xs font-semibold">Start (ISO)<input value={f.start_date} onChange={(e) => set('start_date', e.target.value)} className={inp} data-testid="event-edit-start"/></label>
          <label className="text-xs font-semibold">End (ISO, optional)<input value={f.end_date || ''} onChange={(e) => set('end_date', e.target.value)} className={inp}/></label>
          <label className="text-xs font-semibold">Town<input value={f.town} onChange={(e) => set('town', e.target.value)} className={inp} data-testid="event-edit-town"/></label>
          <label className="text-xs font-semibold">Venue<input value={f.venue} onChange={(e) => set('venue', e.target.value)} className={inp}/></label>
          <label className="sm:col-span-2 text-xs font-semibold">Web link (booking/info)<input value={f.url} onChange={(e) => set('url', e.target.value)} placeholder="https://…" className={inp} data-testid="event-edit-url"/></label>
          <div className="sm:col-span-2">
            <ImageUploadField
              value={f.hero_image}
              onChange={(v) => set('hero_image', v)}
              label="Image"
              testidPrefix="event-edit-image"
            />
          </div>
          {f.hero_image && <img src={f.hero_image} alt="" className="sm:col-span-2 h-32 object-cover rounded-xl"/>}
          <label className="text-xs font-semibold">Price<input value={f.price || ''} onChange={(e) => set('price', e.target.value)} className={inp}/></label>
          <label className="text-xs font-semibold">Category<input value={f.category || ''} onChange={(e) => set('category', e.target.value)} className={inp}/></label>
          <label className="sm:col-span-2 text-xs font-semibold">Summary<textarea rows={2} value={f.summary} onChange={(e) => set('summary', e.target.value)} className={inp}/></label>
          <label className="sm:col-span-2 text-xs font-semibold">Description<textarea rows={4} value={f.description} onChange={(e) => set('description', e.target.value)} className={inp}/></label>
        </div>
        {err && <p className="text-sm text-heritage mt-2" data-testid="event-edit-error">{err}</p>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-full border border-charcoal/20" data-testid="event-edit-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="bg-forest text-cream text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-50" data-testid="event-edit-save">{busy ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export default function AdminEvents() {
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || '';
  const [items, setItems] = useState([]);
  const [quickUrl, setQuickUrl] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState('');
  // Bulk listing discovery
  const [listingUrl, setListingUrl] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState(null);
  const [discoverError, setDiscoverError] = useState('');
  const [fillingImages, setFillingImages] = useState(false);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = React.useRef(null);
  const [savedListings, setSavedListings] = useState([]);
  // The event the admin is about to approve — drives the preview modal.
  const [pendingApproval, setPendingApproval] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [approving, setApproving] = useState(false);

  function load() {
    const q = status ? `?status=${status}` : '';
    api.get(`/admin/events${q}`).then((r) => setItems(r.data.items));
  }
  function loadSavedListings() {
    api.get('/admin/venue-listings').then((r) => setSavedListings(r.data.items));
  }
  useEffect(load, [status]);
  useEffect(() => { loadSavedListings(); }, []);

  async function approve(id) {
    // Show a preview using the public EventCard component so editors catch
    // typos / wrong images / missing data BEFORE it goes live.
    const ev = items.find((x) => x.id === id);
    if (!ev) return;
    setPendingApproval(ev);
  }
  async function confirmApprove() {
    if (!pendingApproval) return;
    setApproving(true);
    try {
      await api.post(`/admin/events/${pendingApproval.id}/approve`);
      setPendingApproval(null);
      load();
    } finally {
      setApproving(false);
    }
  }
  async function reject(id) { await api.post(`/admin/events/${id}/reject`); load(); }
  async function del(id) {
    if (!window.confirm('Delete this event?')) return;
    await api.delete(`/admin/events/${id}`); load();
  }

  async function parseUrl(e) {
    e?.preventDefault();
    setParsing(true); setParseError(''); setParsed(null);
    try {
      const r = await api.post('/admin/events/parse-url', { url: quickUrl });
      setParsed(r.data.parsed);
    } catch (err) {
      setParseError(err.response?.data?.detail || 'Couldn\'t parse that page.');
    } finally { setParsing(false); }
  }

  async function saveParsed() {
    setParseError('');
    if (!parsed?.title || !parsed?.start_date) {
      setParseError("Start date is required. Please fill it in (the page didn't publish a machine-readable date).");
      return;
    }
    try {
      await api.post('/admin/events', parsed);
      setQuickUrl(''); setParsed(null); setParseError('');
      setParams({ status: 'pending' });
      load();
    } catch (err) {
      setParseError(err.response?.data?.detail || err.message || 'Save failed — check the date format (ISO, e.g. 2026-12-25T18:00:00Z).');
    }
  }

  async function scanUploadedFile(ev) {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setScanning(true); setDiscoverError(''); setDiscovered(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post('/admin/events/scan-upload', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      if (!r.data.events?.length) {
        setDiscoverError(`No upcoming events found in "${file.name}" — try a clearer photo or check the file.`);
      } else {
        setDiscovered({
          ...r.data,
          // Duplicates arrive un-ticked — the admin can re-tick to force-add
          events: r.data.events.map((e) => (e._duplicate ? { ...e, _skip: true } : e)),
        });
      }
    } catch (err) {
      setDiscoverError(err.response?.data?.detail || 'Could not read that file.');
    } finally { setScanning(false); }
  }

  async function discoverListing(e) {
    e?.preventDefault();
    setDiscovering(true); setDiscoverError(''); setDiscovered(null);
    try {
      const r = await api.post('/admin/events/discover-from-listing', { url: listingUrl, max_events: 20 });
      setDiscovered(r.data);
    } catch (err) {
      setDiscoverError(err.response?.data?.detail || err.message || "Couldn't fetch that listing page.");
    } finally { setDiscovering(false); }
  }

  function toggleEventInDiscovery(i) {
    setDiscovered({
      ...discovered,
      events: discovered.events.map((e, idx) => idx === i ? { ...e, _skip: !e._skip } : e),
    });
  }
  function updateEventInDiscovery(i, field, value) {
    setDiscovered({
      ...discovered,
      events: discovered.events.map((e, idx) => idx === i ? { ...e, [field]: value } : e),
    });
  }

  async function bulkSaveDiscovered() {
    const toSave = discovered.events
      .filter((e) => !e._skip && e.title && e.start_date)
      .map((e) => (e._duplicate ? { ...e, _force: true } : e)); // ticked duplicate = deliberate override
    if (toSave.length === 0) {
      setDiscoverError('Nothing to save — every event needs a title and start date.');
      return;
    }
    const r = await api.post('/admin/events/bulk-create', { events: toSave });
    setDiscovered(null); setListingUrl('');
    setParams({ status: 'pending' });
    load();
    const dupeNote = r.data.skipped_duplicate ? ` ${r.data.skipped_duplicate} skipped as already in the calendar.` : '';
    window.alert(`Saved ${r.data.saved} events as pending. Skipped ${r.data.skipped}.${dupeNote}`);
  }

  async function saveListingForRescrape() {
    const label = window.prompt('Give this listing a friendly name (e.g. "Space Centre" or "Curve Theatre"):', '');
    if (!label) return;
    try {
      await api.post('/admin/venue-listings', { url: listingUrl, label });
      loadSavedListings();
      window.alert('Saved! This listing will be auto-rescraped every Sunday night.');
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not save.');
    }
  }

  async function rescrapeListing(id) {
    const r = await api.post(`/admin/venue-listings/${id}/rescrape`);
    loadSavedListings();
    window.alert(`Found ${r.data.found} events on the listing; ${r.data.new} new added to pending review.`);
    load();
  }
  async function toggleListing(l) {
    await api.patch(`/admin/venue-listings/${l.id}`, { enabled: !l.enabled });
    loadSavedListings();
  }
  async function deleteListing(l) {
    if (!window.confirm(`Stop auto-scraping ${l.label}?`)) return;
    await api.delete(`/admin/venue-listings/${l.id}`);
    loadSavedListings();
  }
  async function attachPdf(l) {
    const current = l.pdf_url || '';
    const next = window.prompt(
      `PDF fallback URL for ${l.label}.\n\nUsed only when the HTML/RSS scrape returns zero events — handy for venues that only publish a printed programme.\n\nLeave blank to remove.`,
      current,
    );
    if (next === null) return; // user cancelled
    const trimmed = next.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      window.alert('Provide a full http(s) PDF URL.');
      return;
    }
    await api.patch(`/admin/venue-listings/${l.id}`, { pdf_url: trimmed || null });
    loadSavedListings();
  }

  return (
    <div>
      <div className="label text-heritage mb-2">Events</div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display text-4xl">Approve, edit, publish.</h1>
        <button
          onClick={async () => {
            setFillingImages(true);
            try {
              await api.post('/admin/events/backfill-images');
              alert('Started. Missing event images are being filled in the background — event page photo first, then venue logo, brand card only as a last resort. Refresh in a few minutes.');
            } catch { alert('Could not start the image fill.'); }
            finally { setFillingImages(false); }
          }}
          disabled={fillingImages}
          data-testid="events-fill-images"
          className="bg-white border border-charcoal/15 px-4 py-2 rounded-full text-sm font-semibold hover:border-charcoal disabled:opacity-60"
        >
          {fillingImages ? 'Starting…' : 'Fill missing event images'}
        </button>
      </div>

      <HelpCallout
        title="The events calendar — gated by your approval."
        description="Anything the auto-scraper finds (or that you add manually) lands here as Pending. Approve to publish, reject to hide. Once approved, events appear on the public site immediately."
        appearsAt={[
          { label: 'Homepage "This Weekend" strip', where: 'shows up to 3 approved events happening Sat–Sun' },
          { label: '/events page', where: 'every approved upcoming event, grouped by month, filterable by category' },
          { label: 'Friday AI newsletter', where: 'this weekend\'s approved events become the body + items' },
          { label: 'Weekly AI digest prompt', where: 'approved events are referenced by name in the generated copy' },
        ]}
      />

      <div className="flex gap-2 mb-8">
        {STATUS_TABS.map((t) => (
          <button key={t.value} onClick={() => setParams(t.value ? { status: t.value } : {})} data-testid={`event-tab-${t.value || 'all'}`}
            className={`px-5 py-2 rounded-full text-sm font-semibold ${status === t.value ? 'bg-charcoal text-base' : 'bg-white border border-charcoal/15'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <TrashPanel
        resourcePath="/admin/events"
        resourceLabel="Event"
        idField="id"
        nameField="title"
        onChange={load}
      />

      {/* Quick add from URL */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6 mb-6" data-testid="event-quick-add">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="font-display text-xl">Quick add from URL</div>
            <p className="text-muted text-sm mt-1">Paste any event page URL — we&apos;ll grab the title, dates, venue, and image. Confirm and save.</p>
          </div>
        </div>
        <form onSubmit={parseUrl} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <input
            type="url"
            required
            placeholder="https://www.belvoircastle.com/whats-on/christmas-2026"
            value={quickUrl}
            onChange={(ev) => setQuickUrl(ev.target.value)}
            data-testid="quick-add-url-input"
            className="md:col-span-9 w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-3 text-sm"
          />
          <button type="submit" disabled={parsing || !quickUrl}
            data-testid="quick-add-parse-btn"
            className="md:col-span-3 bg-charcoal text-base px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-heritage disabled:opacity-50 transition-colors">
            {parsing ? <RefreshCw size={14} className="animate-spin"/> : <Link2 size={14}/>}
            Fetch event details
          </button>
        </form>
        {parseError && <div className="mt-3 text-heritage text-sm" data-testid="quick-add-error">{parseError}</div>}

        {parsed && (
          <div className="mt-5 border-t border-charcoal/10 pt-5" data-testid="quick-add-preview">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="font-display text-lg">Review &amp; save</div>
              <a
                href={parsed.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quick-add-open-source"
                className="text-xs font-semibold text-heritage hover:underline inline-flex items-center gap-1"
              >
                Open source page ↗
              </a>
            </div>
            {(() => {
              // Flag when the parsed hero image lives on a different domain to the
              // event URL — usually means the page used a stock photo or the
              // scraper grabbed a "related events" thumbnail. Saves the editor
              // from hitting "Save" then noticing the wrong photo on the public site.
              try {
                const heroHost = parsed.hero_image ? new URL(parsed.hero_image).hostname : null;
                const sourceHost = parsed.url ? new URL(parsed.url).hostname : null;
                if (heroHost && sourceHost) {
                  const stripWww = (h) => h.replace(/^www\./, '');
                  if (stripWww(heroHost) !== stripWww(sourceHost)) {
                    return (
                      <div data-testid="quick-add-image-warning" className="mb-3 bg-mustard/15 border border-mustard/50 rounded-xl px-3 py-2 text-xs text-charcoal/85 inline-flex items-center gap-2">
                        ⚠️ Hero image is from <strong>{heroHost}</strong> but the event is on <strong>{sourceHost}</strong> — double-check it&apos;s the right picture.
                      </div>
                    );
                  }
                }
              } catch (_) { /* malformed URL — skip warning */ }
              return null;
            })()}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              {parsed.hero_image && (
                <img src={parsed.hero_image} alt="" className="md:col-span-3 w-full aspect-[4/3] object-cover rounded-xl"/>
              )}
              <div className={`${parsed.hero_image ? 'md:col-span-9' : 'md:col-span-12'} space-y-3`}>
                <label className="block"><div className="label mb-1">Title *</div>
                  <input value={parsed.title || ''} onChange={(ev) => setParsed({ ...parsed, title: ev.target.value })}
                    data-testid="quick-add-title" className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2"/></label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="label mb-1">Start date * (ISO)</div>
                    <input value={parsed.start_date || ''} onChange={(ev) => setParsed({ ...parsed, start_date: ev.target.value })}
                      placeholder="2026-12-25T18:00:00Z" data-testid="quick-add-start"
                      className={`w-full bg-admin border rounded-xl px-3 py-2 ${parsed.start_date ? 'border-charcoal/15' : 'border-heritage ring-2 ring-heritage/20'}`}/>
                    {!parsed.start_date && (
                      <div className="text-heritage text-xs mt-1">⚠️ The page didn&apos;t publish a date — please enter one (e.g. 2026-12-25T18:00:00Z).</div>
                    )}
                  </label>
                  <label className="block"><div className="label mb-1">End date (optional)</div>
                    <input value={parsed.end_date || ''} onChange={(ev) => setParsed({ ...parsed, end_date: ev.target.value })}
                      className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2"/></label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block"><div className="label mb-1">Venue</div>
                    <input value={parsed.venue || ''} onChange={(ev) => setParsed({ ...parsed, venue: ev.target.value })}
                      className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2"/></label>
                  <label className="block"><div className="label mb-1">Town</div>
                    <input value={parsed.town || ''} onChange={(ev) => setParsed({ ...parsed, town: ev.target.value })}
                      className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2"/></label>
                </div>
                <label className="block"><div className="label mb-1">Summary</div>
                  <textarea value={parsed.summary || ''} onChange={(ev) => setParsed({ ...parsed, summary: ev.target.value })}
                    rows={2} className="w-full bg-admin border border-charcoal/15 rounded-xl px-3 py-2"/></label>
                <div className="flex items-center gap-2 pt-2">
                  <button onClick={saveParsed} data-testid="quick-add-save"
                    className="bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep">
                    <Check size={14}/> Save as pending
                  </button>
                  <button onClick={() => { setParsed(null); setQuickUrl(''); }} type="button"
                    className="bg-admin border border-charcoal/15 px-5 py-2.5 rounded-full text-sm font-semibold">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk discover from venue listing page */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-5 md:p-6 mb-8" data-testid="event-bulk-discover">
        <div className="font-display text-xl mb-1">Bulk import from venue listing page</div>
        <p className="text-muted text-sm mb-3">Paste a venue&apos;s &ldquo;what&apos;s on&rdquo; page URL (e.g. https://www.spacecentre.co.uk/whats-on/) and we&apos;ll discover every event linked from it. Review and save in one go.</p>
        <form onSubmit={discoverListing} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <input
            type="url" required
            placeholder="https://www.spacecentre.co.uk/whats-on/"
            value={listingUrl}
            onChange={(ev) => setListingUrl(ev.target.value)}
            data-testid="bulk-discover-url-input"
            className="md:col-span-9 w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-3 text-sm"
          />
          <button type="submit" disabled={discovering || !listingUrl}
            data-testid="bulk-discover-btn"
            className="md:col-span-3 bg-charcoal text-base px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-heritage disabled:opacity-50">
            {discovering ? <RefreshCw size={14} className="animate-spin"/> : <Link2 size={14}/>}
            Discover events
          </button>
        </form>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <span className="text-muted text-xs uppercase tracking-wide font-bold">or</span>
          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={scanUploadedFile} className="hidden" data-testid="event-file-input"/>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={scanning}
            data-testid="event-file-scan-btn"
            className="bg-admin border border-charcoal/15 hover:border-mustard text-charcoal px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {scanning ? <RefreshCw size={14} className="animate-spin"/> : <span>📄</span>}
            {scanning ? 'Reading file with AI…' : 'Upload a PDF or photo of an events list'}
          </button>
          <span className="text-muted text-xs">Flyers, posters, printed programmes — AI extracts each event for your approval.</span>
        </div>
        {discoverError && <div className="mt-3 text-heritage text-sm" data-testid="bulk-discover-error">{discoverError}</div>}

        {/* Saved listings — these are auto-rescraped every Sunday night */}
        {savedListings.length > 0 && (
          <div className="mt-5 border-t border-charcoal/10 pt-5" data-testid="saved-listings-panel">
            <div className="label text-charcoal/60 mb-3">📅 Auto-rescraped every Sunday night</div>
            <div className="space-y-2">
              {savedListings.map((l) => (
                <div key={l.id} data-testid={`saved-listing-${l.id.slice(0,6)}`}
                  className={`bg-stone/40 border border-charcoal/10 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${!l.enabled ? 'opacity-50' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{l.label}</div>
                    <a href={l.url} target="_blank" rel="noreferrer" className="text-xs text-muted truncate hover:text-heritage underline-offset-2 hover:underline">{l.url}</a>
                    {l.pdf_url && (
                      <div className="text-xs text-muted mt-0.5 truncate">
                        📄 PDF fallback: <a href={l.pdf_url} target="_blank" rel="noreferrer" className="hover:text-heritage underline-offset-2 hover:underline">{l.pdf_url}</a>
                      </div>
                    )}
                    {l.last_scraped_at && (
                      <div className="text-xs text-muted mt-1">
                        Last run {new Date(l.last_scraped_at).toLocaleString('en-GB')} · found {l.last_found}, {l.last_new} new
                        {l.last_source === 'pdf' && <span className="ml-1 text-mustard">(via PDF fallback)</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => rescrapeListing(l.id)} className="bg-admin border border-charcoal/15 hover:border-forest text-xs px-3 py-1.5 rounded-full font-semibold inline-flex items-center gap-1.5">
                      <RefreshCw size={11}/> Run now
                    </button>
                    <button onClick={() => attachPdf(l)} data-testid={`attach-pdf-${l.id.slice(0,6)}`} className="bg-admin border border-charcoal/15 hover:border-mustard text-xs px-3 py-1.5 rounded-full font-semibold" title="Attach a PDF fallback">
                      📄 {l.pdf_url ? 'Edit PDF' : 'Add PDF'}
                    </button>
                    <label className="text-xs cursor-pointer inline-flex items-center gap-1.5">
                      <input type="checkbox" checked={l.enabled} onChange={() => toggleListing(l)}/>
                      {l.enabled ? 'On' : 'Paused'}
                    </label>
                    <button onClick={() => deleteListing(l)} className="text-charcoal/40 hover:text-heritage p-1.5">
                      <Trash2 size={13}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {discovered && (
          <div className="mt-5 border-t border-charcoal/10 pt-5" data-testid="bulk-discover-results">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="font-display text-lg">
                Found {discovered.parsed_ok} of {discovered.discovered} events
                {discovered.parsed_err > 0 && <span className="text-muted text-sm font-normal ml-2">({discovered.parsed_err} couldn&apos;t be parsed)</span>}
                {discovered.skipped_past > 0 && <span className="text-muted text-sm font-normal ml-2">({discovered.skipped_past} already finished)</span>}
                {discovered.skipped_no_date > 0 && <span className="text-muted text-sm font-normal ml-2">({discovered.skipped_no_date} no date on source page)</span>}
                {discovered.duplicates > 0 && <span className="text-amber-700 text-sm font-semibold ml-2" data-testid="bulk-duplicates-note">({discovered.duplicates} already in calendar — un-ticked)</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setDiscovered(null)} className="bg-admin border border-charcoal/15 px-4 py-2 rounded-full text-xs font-semibold">Discard</button>
                {discovered.mode !== 'upload' && (
                <button onClick={saveListingForRescrape} data-testid="save-listing-rescrape-btn"
                  className="bg-admin border border-mustard text-charcoal px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2 hover:bg-mustard/20">
                  💾 Save listing for weekly auto-rescrape
                </button>
                )}
                <button onClick={bulkSaveDiscovered} data-testid="bulk-save-all-btn"
                  className="bg-forest text-base px-5 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2 hover:bg-forest-deep">
                  <Check size={12}/> Save all as pending
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {discovered.events.map((ev, i) => (
                <div key={i} className={`bg-stone/40 border rounded-xl p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center ${ev._skip ? 'opacity-40' : ''} ${!ev.start_date ? 'border-heritage/40' : 'border-charcoal/10'}`}>
                  <label className="md:col-span-1 inline-flex items-center justify-center">
                    <input type="checkbox" checked={!ev._skip} onChange={() => toggleEventInDiscovery(i)} data-testid={`bulk-include-${i}`}/>
                  </label>
                  {ev.hero_image && <img src={ev.hero_image} alt="" className="md:col-span-1 w-full aspect-square object-cover rounded-md"/>}
                  <input value={ev.title || ''} onChange={(e) => updateEventInDiscovery(i, 'title', e.target.value)}
                    className={`${ev.hero_image ? 'md:col-span-5' : 'md:col-span-6'} bg-white border border-charcoal/15 rounded-md px-2.5 py-1.5 text-sm`}/>
                  {ev._duplicate && (
                    <div className={`${ev.hero_image ? 'md:col-span-5' : 'md:col-span-6'} md:col-start-2 -mt-2 text-xs text-amber-700 font-semibold`} data-testid={`bulk-duplicate-${i}`}>
                      ⚠️ Already in calendar as “{ev._duplicate_of}” — tick the box to add anyway
                    </div>
                  )}
                  <input value={ev.start_date || ''} onChange={(e) => updateEventInDiscovery(i, 'start_date', e.target.value)}
                    placeholder="YYYY-MM-DDTHH:MM:SSZ" data-testid={`bulk-start-${i}`}
                    className={`md:col-span-4 bg-white border rounded-md px-2.5 py-1.5 text-sm font-mono ${ev.start_date ? 'border-charcoal/15' : 'border-heritage'}`}/>
                  <div className="md:col-span-1 text-xs text-muted text-right">{ev._date_source === 'iso-html' ? 'auto-iso' : ev._date_source === 'body-text' ? 'auto-text' : ev.start_date ? 'auto' : '⚠️ no date'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {items.map((e) => (
          <div key={e.id} className="bg-white border border-charcoal/10 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" data-testid={`event-row-${e.slug}`}>
            <img src={e.hero_image} alt="" className="md:col-span-2 w-full aspect-[4/3] object-cover rounded-xl"/>
            <div className="md:col-span-6">
              <div className="label text-charcoal/50 mb-1">{new Date(e.start_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} · {e.town}</div>
              <h3 className="font-display text-xl mb-1">{e.title}</h3>
              <p className="text-muted text-sm line-clamp-2">{e.summary}</p>
              <div className="mt-2 text-xs text-charcoal/50">Source: {e.source} · Status: <span className={`font-semibold ${e.status === 'approved' ? 'text-forest' : e.status === 'pending' ? 'text-heritage' : 'text-muted'}`}>{e.status}</span></div>
            </div>
            <div className="md:col-span-4 flex flex-wrap gap-2 justify-end">
              <button onClick={() => setEditingEvent(e)} data-testid={`event-edit-${e.slug}`} className="border border-charcoal/15 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 hover:border-forest">
                <Pencil size={14}/> Edit
              </button>
              {e.status !== 'approved' && (
                <button onClick={() => approve(e.id)} data-testid={`event-approve-${e.slug}`} className="bg-forest text-base px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 hover:bg-forest-deep">
                  <Check size={14}/> Approve
                </button>
              )}
              {e.status !== 'rejected' && (
                <button onClick={() => reject(e.id)} data-testid={`event-reject-${e.slug}`} className="border border-charcoal/15 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 hover:border-charcoal">
                  <X size={14}/> Reject
                </button>
              )}
              <button onClick={() => del(e.id)} data-testid={`event-delete-${e.slug}`} className="border border-heritage/30 text-heritage px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1 hover:bg-heritage/5">
                <Trash2 size={14}/>
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-16 text-muted">No events.</div>}
      </div>

      {editingEvent && (
        <EventEditModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={async () => { setEditingEvent(null); await load(); }}
        />
      )}

      {pendingApproval && (
        <div
          className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !approving && setPendingApproval(null)}
          data-testid="approve-preview-modal"
        >
          <div
            className="bg-stone rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-4 flex items-center justify-between z-10">
              <div>
                <div className="label text-heritage">Preview before publishing</div>
                <div className="font-display text-lg">This is what the public will see.</div>
              </div>
              <button
                onClick={() => setPendingApproval(null)}
                disabled={approving}
                className="p-2 hover:bg-stone rounded-lg disabled:opacity-40"
                data-testid="approve-preview-close"
              >
                <X size={18}/>
              </button>
            </div>

            <div className="p-6">
              <EventCard event={pendingApproval} as_preview />

              {/* Warn if the event has obvious gaps that will look broken on the public site */}
              {(() => {
                const warnings = [];
                if (!pendingApproval.hero_image) warnings.push('No hero image — a category placeholder will be shown.');
                if (!pendingApproval.summary) warnings.push('No summary — the card will look bare.');
                if (!pendingApproval.town) warnings.push('No town set.');
                if (!pendingApproval.start_date) warnings.push('No start date — this will break the events grid.');
                if (warnings.length === 0) return null;
                return (
                  <div className="mt-5 bg-mustard/15 border border-mustard/50 rounded-xl p-3 text-xs text-charcoal/85" data-testid="approve-preview-warnings">
                    <div className="font-semibold mb-1">Heads up:</div>
                    <ul className="list-disc list-inside space-y-0.5">
                      {warnings.map((w) => <li key={w}>{w}</li>)}
                    </ul>
                  </div>
                );
              })()}
              {/* Missing town? Let the admin set it right here before approving */}
              {!pendingApproval.town && (
                <div className="mt-4 bg-heritage/10 border border-heritage/40 rounded-xl p-3" data-testid="approve-town-fix">
                  <div className="text-xs font-semibold mb-1.5">No town set — add one before publishing:</div>
                  <div className="flex gap-2">
                    <input
                      value={pendingApproval._town_fix || ''}
                      onChange={(ev) => setPendingApproval({ ...pendingApproval, _town_fix: ev.target.value })}
                      placeholder="e.g. Loughborough"
                      data-testid="approve-town-input"
                      className="flex-1 bg-admin border border-charcoal/15 rounded-xl px-3 py-2 text-sm"
                    />
                    <button
                      onClick={async () => {
                        const t = (pendingApproval._town_fix || '').trim();
                        if (!t) return;
                        try {
                          await api.patch(`/admin/events/${pendingApproval.id}`, { town: t });
                          setPendingApproval({ ...pendingApproval, town: t });
                          load();
                        } catch (e2) { alert(e2.response?.data?.detail || 'Could not save town'); }
                      }}
                      data-testid="approve-town-save"
                      className="bg-charcoal text-base text-xs font-semibold px-4 rounded-xl"
                    >
                      Save town
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-4 flex items-center justify-end gap-2">
              <button
                onClick={() => setPendingApproval(null)}
                disabled={approving}
                data-testid="approve-preview-back"
                className="bg-admin border border-charcoal/15 px-5 py-2.5 rounded-full text-sm font-semibold hover:border-charcoal disabled:opacity-50"
              >
                Back to edit
              </button>
              <button
                onClick={confirmApprove}
                disabled={approving}
                data-testid="approve-preview-confirm"
                className="bg-forest text-base px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep disabled:opacity-50"
              >
                {approving ? <RefreshCw size={14} className="animate-spin"/> : <Check size={14}/>}
                Confirm &amp; publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
